import _ from 'lodash';

import moment from 'moment';

import { NextPromotion, Promotion, RetailerPromotionOrder } from '@/types/promotion';
import { getLegacySurveyUrl } from '@config/getEnviroment';

export const LIMIT = 9;
export const PROMO_STOCK_ALERT = 10;
export const EXPIRED = 'EXPIRED';
export const EXPIRED_VERIFYING = 'EXPIRED_VERIFYING';
export const REQUESTED = 'REQUESTED';
export const REDEEMED = 'REDEEMED';
export const CANCELLED = 'CANCELLED';
export const EVALUATED = 'EVALUATED';

export const SIDE_LIST_SKELETON_LIMIT = 4;

export const ROUTE_STATUS = {
  PRE_AVAILABLE: 'PRE_SURVEY_REQUEST',
  PRE_EXTRA_AVAILABLE: 'PRE_EXTRA_SURVEY_REQUEST',
  PRE_FINISHED: 'ORDER_GENERATED',
  POST_AVAILABLE: 'POST_SURVEY_REQUEST',
  POST_EXTRA_AVAILABLE: 'POST_EXTRA_SURVEY_REQUEST',
  POST_FINISHED: 'ORDER_EVALUATED',
  PRE_SHUFFLE_AVAILABLE: 'PRE_SHUFFLE_REQUESTED',
  PRE_SHUFFLE_FINISHED: 'PRE_SHUFFLE_COMPLETED',
  POST_SHUFFLE_AVAILABLE: 'POST_SHUFFLE_REQUESTED',
  POST_SHUFFLE_FINISHED: 'POST_SHUFFLE_COMPLETED',
  RR_AVAILABLE: 'RATING_REVIEW_REQUEST',
};

export enum WhereToShow {
  PageList = 'page',
  SideList = 'side',
  Detail = 'detail',
  Mobile = 'mobile',
}

export enum PromotionTypes {
  WithPoints = 'SAZ NEXT WITH POINTS',
  RedemptionPoint = 'WITHDRAWAL AT REDEMPTION POINT',
  Delivery = 'DELIVERY OLD',
  Ecommerce = 'ECOMMERCE',
}

export const getCouponLabel = (state: string) => {
  switch (state) {
    case EXPIRED:
    case EXPIRED_VERIFYING: {
      return 'pages.promotions.card.expired';
    }
    case REDEEMED: {
      return 'pages.promotions.card.evaluate';
    }
    case REQUESTED: {
      return 'pages.promotions.card.exchange';
    }
    default:
      return state;
  }
};

export const getName = (name: string, max?: number) => {
  const MAX_LENGTH = max || 35;

  if (name.length > MAX_LENGTH) {
    return `${name.substring(0, MAX_LENGTH)}...`;
  }

  return name;
};

export const parseSegmentatedPromotions = (data: any): Promotion[] => {
  const list: any = [];

  data.forEach((item: any) => {
    list.push(item?.promotion);
  });

  return list;
};

export const isPromoUnavailable = (promotion: Promotion | NextPromotion): boolean => {
  const promoType = promotion?.promotionType?.name;

  switch (promoType) {
    case PromotionTypes.WithPoints:
      return (
        promotion.promotionStock.availableStock === 0 ||
        (promotion.couponCodesStats && promotion.couponCodesStats.notDistributed === 0)
      );
    case PromotionTypes.RedemptionPoint:
    case PromotionTypes.Delivery:
      return promotion.promotionStock.availableStock <= 0;
    default:
      return false;
  }
};

export const isPromoUnavailableByRetailer = (promotion: NextPromotion, retailer?: string): boolean => {
  if (retailer) {
    const getRetailerNode = _.find(promotion?.pickupPointStocks, (n: any) => n.pickupPoint?.id === retailer);

    if (getRetailerNode) {
      return getRetailerNode?.availableStock <= 0;
    }
  }

  return false;
};

export const calculatePercentage = (total: number, partial: number) =>
  parseInt(((partial / total) * 100).toFixed(), 10);

export const parseRetailerPromoForOrder = (
  selector: RetailerPromotionOrder[],
  retailerId: string,
  promotionId: string
) => {
  const check = selector.filter((item) => item.promotionId === promotionId);
  let output = selector;

  if (check.length) {
    output = selector.filter((item) => item.promotionId !== promotionId);
  }

  output.push({ retailerId, promotionId });

  return output;
};

export const deleteRetailerPromoForOrder = (selector: RetailerPromotionOrder[], promotionId: string) => {
  return selector.filter((item) => item.promotionId !== promotionId);
};

export const getRetailerPromoForOrder = (selector: RetailerPromotionOrder[], promotionId: string) => {
  const check = selector.filter((item) => item.promotionId === promotionId);

  return check?.[0]?.retailerId ?? null;
};

export const getSurveyPostUrl = (promotionId: string, userId?: string, date?: string) => {
  return date
    ? `${getLegacySurveyUrl()}survey/post/${promotionId}/${userId}/web/panama-spa/${date}`
    : `${getLegacySurveyUrl()}survey/post/${promotionId}/${userId}/web/panama-spa`;
};

export const errorDictionary = (key: string) => {
  switch (key) {
    case 'LimitOfRequestedPromotionsReached':
      return 'pages.promotions.errors.limitRequested';
    case 'UserPoints-TransactionNotAllowed':
      return 'pages.promotions.errors.noCredits';
    case 'Order-OrderExists':
      return 'pages.promotions.errors.alreadyRequested';
    case 'Promotion-Inactive':
      return 'pages.promotions.errors.promotionInactive';
    case 'Retailer-NotFoundRetailer':
      return 'pages.promotions.errors.retailerNotFound';
    case 'Promotion-NotEnoughStock':
      return 'pages.promotions.errors.noStock';
    case 'RetailerStock-NotEnoughStock':
      return 'pages.promotions.errors.retailerNoStock';
    default:
      return 'pages.promotions.errors.default';
  }
};

export const calculateDaysToExpire = (expirationDate: string) => {
  const today = moment(new Date());
  const date = moment(expirationDate).isUTC() ? moment(expirationDate).local() : moment(expirationDate);

  date.format('DD MMMM YYYY');
  today.format('DD MMMM YYYY');

  return date.diff(today, 'days');
};

export const rewardDictionary = (reward: string) => {
  switch (reward) {
    case 'ADD_EXPIRING_POINTS_ON_FIRST_EVALUATION':
      return 'rewards.specialOctober24';
    default:
      return '';
  }
};
